/**
 * @name DualPrinterConfigModal
 * @author Keven Adan Bezerra | kevenadanb
 * @description Modal para configuração de duas impressoras (Totem + Cozinha)
 * @param {DualPrinterConfigModalProps} props - Propriedades do modal
 * @returns {JSX.Element} Modal de configuração de múltiplas impressoras
 * @package src/components/modals/DualPrinterConfigModal
 */

import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Kitchen as KitchenIcon,
  Print as PrintIcon,
  Restaurant as RestaurantIcon,
  Settings as SettingsIcon,
  TabletMac as TabletIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import React, { memo, useEffect, useState } from "react";
import { useDualReceiptPrinter } from "../../hooks/useDualReceiptPrinter";
import { useLoading } from "../../hooks/useLoading";
import { useMultiplePrinters } from "../../hooks/useMultiplePrinters";

interface DualPrinterConfigModalProps {
  /** Se o modal está aberto */
  open: boolean;
  /** Função para fechar o modal */
  onClose: () => void;
}

/**
 * Modal para configuração de duas impressoras (Totem + Cozinha)
 */
const DualPrinterConfigModal: React.FC<DualPrinterConfigModalProps> = memo(
  ({ open, onClose }) => {
    const { loadingStates } = useLoading();

    const {
      isConnected,
      isConnecting,
      printers,
      selectedPrinters,
      connectToQZTray,
      getPrinters,
      setTotemPrinter,
      setCozinhaPrinter,
      setBothPrinters,
      testPrint,
      isQZTrayInstalled,
      printMode,
      togglePrintMode,
      getCurrentPrintMode,
    } = useMultiplePrinters();

    const { imprimirTesteRecibo, imprimirTesteComanda } =
      useDualReceiptPrinter();

    // Estados locais
    const [localTotemPrinter, setLocalTotemPrinter] = useState<string>("");
    const [localCozinhaPrinter, setLocalCozinhaPrinter] = useState<string>("");
    const [singlePrinter, setSinglePrinter] = useState<string>("");
    const [configMode, setConfigMode] = useState<"dual" | "single">("dual");
    const [hasTriedConnection, setHasTriedConnection] = useState(false);

    /**
     * Carrega impressoras ao abrir o modal
     */
    useEffect(() => {
      if (open && !hasTriedConnection) {
        setHasTriedConnection(true);
        handleLoadPrinters();
      }
    }, [open, hasTriedConnection]);

    /**
     * Sincroniza impressoras selecionadas
     */
    useEffect(() => {
      if (selectedPrinters.printerTotem) {
        setLocalTotemPrinter(selectedPrinters.printerTotem);
      }
      if (selectedPrinters.printerCozinha) {
        setLocalCozinhaPrinter(selectedPrinters.printerCozinha);
      }
    }, [selectedPrinters]);

    /**
     * Carrega lista de impressoras
     */
    const handleLoadPrinters = async (): Promise<void> => {
      await getPrinters();
    };

    /**
     * Salva configuração das impressoras
     */
    const handleSavePrinters = (): void => {
      if (configMode === "single" && singlePrinter) {
        setBothPrinters(singlePrinter);
      } else if (configMode === "dual") {
        if (localTotemPrinter) {
          setTotemPrinter(localTotemPrinter);
        }
        if (localCozinhaPrinter) {
          setCozinhaPrinter(localCozinhaPrinter);
        }
      }
      onClose();
    };

    /**
     * Testa impressora específica
     */
    const handleTestPrint = async (printerName: string): Promise<void> => {
      await testPrint(printerName);
    };

    /**
     * Testa impressão de recibo do totem
     */
    const handleTestTotemReceipt = async (): Promise<void> => {
      await imprimirTesteRecibo();
    };

    /**
     * Testa impressão de comanda da cozinha
     */
    const handleTestCozinhaReceipt = async (): Promise<void> => {
      await imprimirTesteComanda();
    };

    /**
     * Renderiza status de conexão
     */
    const renderConnectionStatus = () => {
      if (!isQZTrayInstalled()) {
        return (
          <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2">
              QZ Tray não está instalado ou carregado
            </Typography>
          </Alert>
        );
      }

      if (isConnecting) {
        return (
          <Alert
            severity="info"
            icon={<CircularProgress size={20} />}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2">Conectando ao QZ Tray...</Typography>
          </Alert>
        );
      }

      if (isConnected) {
        return (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2">Conectado ao QZ Tray</Typography>
          </Alert>
        );
      }

      return (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2">Não conectado ao QZ Tray</Typography>
        </Alert>
      );
    };

    /**
     * Renderiza pré-requisitos
     */
    const renderPrerequisites = () => (
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <SettingsIcon />
          Pré-requisitos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Para utilizar múltiplas impressoras, certifique-se de que:
        </Typography>

        <Stack spacing={1}>
          <Typography
            variant="body2"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            • Java Runtime Environment (JRE) instalado
          </Typography>
          <Typography
            variant="body2"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            • QZ Tray instalado e executando
          </Typography>
          <Typography
            variant="body2"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            • Pelo menos duas impressoras configuradas no sistema
          </Typography>
        </Stack>
      </Box>
    );

    /**
     * Renderiza controle de modo de impressão
     */
    const renderPrintModeToggle = () => {
      const currentMode = getCurrentPrintMode();

      return (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <PrintIcon />
            Modo de Impressão Ativo
          </Typography>

          <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={printMode === "dual"}
                  onChange={togglePrintMode}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {printMode === "dual"
                      ? "Duas Impressoras"
                      : "Impressora Única"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {printMode === "dual"
                      ? "Via do pedido + comanda em impressoras separadas"
                      : "Apenas via do pedido em uma impressora"}
                  </Typography>
                </Box>
              }
            />
          </Card>

          <Alert
            severity={currentMode === "single" ? "info" : "success"}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2">
              <strong>Modo Atual:</strong>{" "}
              {currentMode === "single"
                ? "Impressora Única"
                : "Duas Impressoras"}
            </Typography>
            {currentMode === "single" && printMode === "dual" && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                Usando impressora única automaticamente (mesma impressora
                configurada para ambas)
              </Typography>
            )}
          </Alert>
        </Box>
      );
    };

    /**
     * Renderiza configuração de impressora específica
     */
    const renderPrinterConfig = (
      title: string,
      icon: React.ReactNode,
      value: string,
      onChange: (value: string) => void,
      onTest: () => Promise<void>,
      onTestReceipt?: () => Promise<void>,
      color: "primary" | "secondary" = "primary",
    ) => (
      <Card variant="outlined">
        <CardHeader
          avatar={icon}
          title={title}
          titleTypographyProps={{ variant: "h6", fontSize: "1rem" }}
          sx={{ pb: 1 }}
        />
        <CardContent sx={{ pt: 0 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Selecionar Impressora</InputLabel>
            <Select
              value={value}
              onChange={(e) => onChange(e.target.value)}
              label="Selecionar Impressora"
              disabled={!isConnected || printers.length === 0}
            >
              {printers.map((printer) => (
                <MenuItem key={printer.name} value={printer.name}>
                  {printer.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {value && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={onTest}
                size="small"
                disabled={!value}
                color={color}
              >
                Teste Simples
              </Button>
              {onTestReceipt && (
                <Button
                  variant="outlined"
                  startIcon={<RestaurantIcon />}
                  onClick={onTestReceipt}
                  size="small"
                  disabled={!value}
                  color={color}
                >
                  Teste Completo
                </Button>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );

    /**
     * Renderiza seletor de modo de configuração
     */
    const renderModeSelector = () => (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Modo de Configuração
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Card
              variant={configMode === "dual" ? "elevation" : "outlined"}
              sx={{
                cursor: "pointer",
                border: configMode === "dual" ? 2 : 1,
                borderColor: configMode === "dual" ? "primary.main" : "divider",
                height: "100%",
                minHeight: 140,
                display: "flex",
                flexDirection: "column",
              }}
              onClick={() => setConfigMode("dual")}
            >
              <CardContent
                sx={{ flex: 1, display: "flex", flexDirection: "column" }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <SettingsIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Duas Impressoras
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flex: 1 }}
                >
                  Configure impressoras separadas para via do pedido (totem) e
                  comandas (cozinha)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Card
              variant={configMode === "single" ? "elevation" : "outlined"}
              sx={{
                cursor: "pointer",
                border: configMode === "single" ? 2 : 1,
                borderColor:
                  configMode === "single" ? "primary.main" : "divider",
                height: "100%",
                minHeight: 140,
                display: "flex",
                flexDirection: "column",
              }}
              onClick={() => setConfigMode("single")}
            >
              <CardContent
                sx={{ flex: 1, display: "flex", flexDirection: "column" }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <PrintIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Impressora Única
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flex: 1 }}
                >
                  Use a mesma impressora para via do pedido e comandas
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );

    /**
     * Renderiza configuração para impressora única
     */
    const renderSinglePrinterConfig = () => (
      <Box>
        <Typography variant="h6" gutterBottom>
          Configuração de Impressora Única
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderPrinterConfig(
              "Impressora para Via do Pedido e Comandas",
              <PrintIcon color="primary" />,
              singlePrinter,
              setSinglePrinter,
              () => handleTestPrint(singlePrinter),
              undefined,
              "primary",
            )}
          </Grid>
        </Grid>
      </Box>
    );

    /**
     * Renderiza configuração para duas impressoras
     */
    const renderDualPrinterConfig = () => (
      <Box>
        <Typography variant="h6" gutterBottom>
          Configuração de Duas Impressoras
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {renderPrinterConfig(
              "Impressora do Totem",
              <TabletIcon color="primary" />,
              localTotemPrinter,
              setLocalTotemPrinter,
              () => handleTestPrint(localTotemPrinter),
              handleTestTotemReceipt,
              "primary",
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderPrinterConfig(
              "Impressora da Cozinha",
              <KitchenIcon color="secondary" />,
              localCozinhaPrinter,
              setLocalCozinhaPrinter,
              () => handleTestPrint(localCozinhaPrinter),
              handleTestCozinhaReceipt,
              "secondary",
            )}
          </Grid>
        </Grid>
      </Box>
    );

    /**
     * Renderiza seletor de impressoras
     */
    const renderPrinterSelector = () => {
      if (!isConnected) {
        return (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Conecte ao QZ Tray para listar impressoras
            </Typography>
            <Button
              variant="outlined"
              onClick={connectToQZTray}
              sx={{ mt: 2 }}
              disabled={isConnecting || !isQZTrayInstalled()}
            >
              Conectar ao QZ Tray
            </Button>
          </Box>
        );
      }

      if (loadingStates["qz-printers"]) {
        return (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Carregando impressoras...
            </Typography>
          </Box>
        );
      }

      if (printers.length === 0) {
        return (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Nenhuma impressora encontrada. Verifique se há impressoras
              instaladas no sistema.
            </Typography>
          </Alert>
        );
      }

      return (
        <Box>
          {renderModeSelector()}
          {configMode === "single"
            ? renderSinglePrinterConfig()
            : renderDualPrinterConfig()}
        </Box>
      );
    };

    /**
     * Renderiza resumo da configuração
     */
    const renderConfigSummary = () => {
      const hasConfig =
        (configMode === "single" && singlePrinter) ||
        (configMode === "dual" && (localTotemPrinter || localCozinhaPrinter));

      if (!hasConfig) return null;

      return (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Resumo da Configuração
          </Typography>
          <Stack spacing={1}>
            {configMode === "single" && singlePrinter && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PrintIcon color="primary" fontSize="small" />
                <Typography variant="body2">
                  <strong>Impressora Única:</strong> {singlePrinter}
                </Typography>
              </Box>
            )}
            {configMode === "dual" && localTotemPrinter && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TabletIcon color="primary" fontSize="small" />
                <Typography variant="body2">
                  <strong>Via do Pedido (Totem):</strong> {localTotemPrinter}
                </Typography>
              </Box>
            )}
            {configMode === "dual" && localCozinhaPrinter && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <KitchenIcon color="secondary" fontSize="small" />
                <Typography variant="body2">
                  <strong>Comandas (Cozinha):</strong> {localCozinhaPrinter}
                </Typography>
              </Box>
            )}
          </Stack>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Como funciona:</strong>
              <br />• <strong>Via do Pedido</strong> será impressa na impressora
              do totem (para o aluno)
              <br />• <strong>Comandas</strong> serão impressas na impressora da
              cozinha (para preparo)
              <br />• Ambas as impressões ocorrem automaticamente após confirmar
              o pedido
            </Typography>
          </Alert>
        </Box>
      );
    };

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <PrintIcon />
            Configuração de Múltiplas Impressoras
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {renderConnectionStatus()}

          <Divider sx={{ my: 2 }} />

          {renderPrintModeToggle()}

          <Divider sx={{ my: 2 }} />

          {renderPrerequisites()}

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom>
            Configurar Impressoras
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure uma impressora para via do pedido (totem) e outra para
            comandas (cozinha):
          </Typography>

          {renderPrinterSelector()}

          {renderConfigSummary()}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleSavePrinters}
            variant="contained"
            disabled={
              !isConnected ||
              (configMode === "single" && !singlePrinter) ||
              (configMode === "dual" &&
                !localTotemPrinter &&
                !localCozinhaPrinter)
            }
          >
            Salvar Configuração
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

DualPrinterConfigModal.displayName = "DualPrinterConfigModal";

export default DualPrinterConfigModal;
